import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETIQUETA_TIPO_TRANSACCION_PARTE,
  formatearFechaHora,
  formatearMoneda,
  type Pagina,
  type TipoTransaccionParte,
} from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import {
  AccionesDialogo,
  Aviso,
  Cargando,
  Dialogo,
  DialogoConfirmar,
  Etiqueta,
  Indicador,
  Paginacion,
  SeleccionMultiple,
  Vacio,
  mensajeError,
} from '../componentes/piezas.js';
import { useCatalogo, useGruposOpciones, useTiposActivo } from '../componentes/selectores.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { BotonImportarCsv } from '../componentes/importar-csv.js';
import { BotonEscanearQr, EscanerQr, hayCamara } from '../componentes/camara.js';
import { IconoLupa, IconoMas } from '../componentes/iconos.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

// ─── Tipos de la API ─────────────────────────────────────────────────────────

interface Referencia {
  id: string;
  nombre: string;
}

interface StockEnUbicacion {
  ubicacionId: string;
  ubicacionNombre: string;
  ubicacionRaiz: string | null;
  area: string | null;
  unidadesEnExistencia: number;
  minimoEnExistencia: number;
  maximoEnExistencia: number | null;
  necesitaReposicion: boolean;
}

interface Parte {
  id: string;
  numero: number;
  nombre: string;
  idPieza: string;
  numeroParte: string | null;
  descripcion: string | null;
  costoUnitarioPen: string;
  codigoQr: string | null;
  tiposParte: Referencia[];
  tiposActivoCompatibles: Referencia[];
  activosCompatibles: Referencia[];
  gruposACargo: Referencia[];
  stock: StockEnUbicacion[];
  totalEnExistencia: number;
  ubicacionesEnAlerta: number;
  eliminadoEl: string | null;
}

interface Transaccion {
  id: string;
  tipo: TipoTransaccionParte;
  ubicacionNombre: string;
  ubicacionDestinoNombre: string | null;
  cantidad: number;
  costoUnitarioPen: string;
  costoTotalPen: string;
  ordenTrabajoId: string | null;
  ordenTrabajoNumero: number | null;
  usuario: string | null;
  fechaHora: string;
  nota: string | null;
}

interface FilaReposicion {
  parteId: string;
  parteNombre: string;
  idPieza: string;
  ubicacionId: string;
  ubicacionNombre: string;
  ubicacionRaiz: string | null;
  area: string | null;
  unidadesEnExistencia: number;
  minimoEnExistencia: number;
  sugeridoReponer: number;
  gruposACargo: string[];
}

// ─── Lista de partes (§6.1, §6.2) ────────────────────────────────────────────

export function ListaPartes(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [pagina, setPagina] = useState(1);
  const [buscar, setBuscar] = useState('');
  const [soloReposicion, setSoloReposicion] = useState(false);
  const [creando, setCreando] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['partes', pagina, buscar, soloReposicion],
    queryFn: () =>
      api.get<Pagina<Parte>>(
        `/partes${consulta({
          pagina,
          tamano: 25,
          buscar,
          necesitaReposicion: soloReposicion ? true : '',
        })}`,
      ),
  });

  // El indicador se calcula sobre pares parte+ubicación, no sobre partes: es la
  // unidad en la que §6.2 define la rotura.
  const { data: reposicion } = useQuery({
    queryKey: ['reposicion'],
    queryFn: () => api.get<FilaReposicion[]>('/inventario/reposicion'),
  });

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div>
          <h1>Partes</h1>
          <p className="cabecera-subtitulo">
            Catálogo de repuestos con existencias por sede. La alerta de reposición se evalúa en cada
            ubicación, nunca sobre el total.
          </p>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar partes"
            placeholder="Buscar por nombre, ID de pieza o número…"
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
          />
        </div>
        {puede('parte:escribir') ? (
          <>
            <BotonImportarCsv ruta="partes" alTerminar={() => void cliente.invalidateQueries({ queryKey: ['partes'] })} />
            <button type="button" className="boton oculto-en-movil" onClick={() => setCreando(true)}>
              Nueva parte
            </button>
            <button type="button" className="boton-flotante-crear" aria-label="Nueva parte" onClick={() => setCreando(true)}>
              <IconoMas />
            </button>
          </>
        ) : null}
      </div>

      <div className="rejilla-indicadores">
        <Indicador titulo="PARTES EN EL CATÁLOGO" valor={data?.total ?? '—'} />
        <Indicador
          titulo="UBICACIONES BAJO EL MÍNIMO"
          valor={reposicion?.length ?? '—'}
          nota={
            reposicion && reposicion.length > 0
              ? 'Hay roturas de stock en sedes concretas'
              : 'Todas las sedes por encima de su mínimo'
          }
        />
        <Indicador
          titulo="VALOR EN EXISTENCIA (PÁGINA)"
          valor={formatearMoneda(
            (data?.datos ?? []).reduce(
              (n, p) => n + p.totalEnExistencia * Number(p.costoUnitarioPen),
              0,
            ),
          )}
          className="col-ocultar-movil"
        />
      </div>

      <div className="filtros">
        <button
          type="button"
          className={soloReposicion ? 'boton pequeno' : 'boton secundario pequeno'}
          onClick={() => {
            setSoloReposicion((v) => !v);
            setPagina(1);
          }}
        >
          Necesita reposición
        </button>
        <BotonEscanearQr
          etiqueta="Escanear QR de parte"
          entidades={['PARTE']}
          alResolver={(r) => navegar(`/partes/${r.entidadId}`)}
        />
      </div>

      {reposicion && reposicion.length > 0 && !soloReposicion ? (
        <Aviso tono="atencion">
          {reposicion.length} ubicación(es) por debajo de su mínimo. Ver{' '}
          <EnlaceRuta to="/reposicion">la lista de reposición</EnlaceRuta>.
        </Aviso>
      ) : null}

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.datos.length === 0 ? (
          <Vacio
            titulo={soloReposicion ? 'Ninguna parte necesita reposición' : 'Sin partes en esta vista'}
          />
        ) : null}

        {data && data.datos.length > 0 ? (
          <>
            {esMovil ? (
              <div className="tarjetas-ot">
                {data.datos.map((p) => (
                  <div
                    key={p.id}
                    className="tarjeta-ot"
                    role="button"
                    tabIndex={0}
                    onClick={() => navegar(`/partes/${p.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navegar(`/partes/${p.id}`);
                    }}
                  >
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <span className="numero-registro">#{p.numero}</span>
                        <span style={{ fontWeight: 700 }}>{formatearMoneda(p.costoUnitarioPen)}</span>
                      </div>
                      <div className="tarjeta-ot-titulo">{p.nombre}</div>
                      {p.numeroParte ? <div className="tarjeta-ot-nota">N.º de parte {p.numeroParte}</div> : null}
                      <div className="tarjeta-ot-datos">
                        <span>
                          {p.totalEnExistencia} en {p.stock.length} ubicación(es)
                        </span>
                        {p.ubicacionesEnAlerta > 0 ? (
                          <Etiqueta tono="roja">{p.ubicacionesEnAlerta} bajo el mínimo</Etiqueta>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>N.º</th>
                      <th>Parte</th>
                      <th>ID de pieza</th>
                      <th>Compatibilidad</th>
                      <th>Existencias</th>
                      <th>Costo unitario</th>
                      <th>Grupo a cargo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.datos.map((p) => (
                      <tr key={p.id}>
                        <td className="numero-registro">#{p.numero}</td>
                        <td>
                          <EnlaceRuta to={`/partes/${p.id}`} style={{ fontWeight: 600 }}>
                            {p.nombre}
                          </EnlaceRuta>
                          {p.numeroParte ? (
                            <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                              N.º de parte {p.numeroParte}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>{p.idPieza}</td>
                        <td>
                          {/* §6.2: la compatibilidad dejó de vivir en el nombre. */}
                          {p.tiposActivoCompatibles.length + p.activosCompatibles.length === 0 ? (
                            <span style={{ color: 'var(--texto-tenue)' }}>Sin definir</span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {p.tiposActivoCompatibles.map((t) => (
                                <Etiqueta key={t.id} tono="azul">{t.nombre}</Etiqueta>
                              ))}
                              {p.activosCompatibles.length > 0 ? (
                                <Etiqueta tono="gris">{p.activosCompatibles.length} activo(s)</Etiqueta>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ fontWeight: 700 }}>{p.totalEnExistencia}</span>{' '}
                          <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}>
                            en {p.stock.length} ubicación(es)
                          </span>
                          {p.ubicacionesEnAlerta > 0 ? (
                            <div style={{ marginTop: 3 }}>
                              <Etiqueta tono="roja">
                                {p.ubicacionesEnAlerta} bajo el mínimo
                              </Etiqueta>
                            </div>
                          ) : null}
                        </td>
                        <td>{formatearMoneda(p.costoUnitarioPen)}</td>
                        <td>{p.gruposACargo.map((g) => g.nombre).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion
              pagina={data.pagina}
              tamano={data.tamano}
              total={data.total}
              onCambiar={setPagina}
            />
          </>
        ) : null}
      </div>

      {creando ? (
        <FormularioParte
          parte={null}
          alCerrar={() => setCreando(false)}
          alGuardar={async () => {
            await cliente.invalidateQueries({ queryKey: ['partes'] });
            setCreando(false);
          }}
        />
      ) : null}
    </>
  );
}

// ─── Necesita reposición (§6.2, §17.4) ───────────────────────────────────────

export function Reposicion(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [reponiendo, setReponiendo] = useState<FilaReposicion | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['reposicion'],
    queryFn: () => api.get<FilaReposicion[]>('/inventario/reposicion'),
  });

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Necesita reposición</h1>
          <p className="cabecera-subtitulo">
            Pares de parte y ubicación por debajo de su mínimo. Una parte puede estar en cero en una
            sede mientras el total agregado parece suficiente: ahí es donde se rompe la operación.
          </p>
        </div>
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo="Ninguna ubicación por debajo de su mínimo"
            detalle="Todas las sedes tienen existencias por encima del umbral configurado."
          />
        ) : null}

        {data && data.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {data.map((f) => (
                <div
                  key={`${f.parteId}-${f.ubicacionId}`}
                  className="tarjeta-ot"
                  role="button"
                  tabIndex={0}
                  onClick={() => navegar(`/partes/${f.parteId}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navegar(`/partes/${f.parteId}`);
                  }}
                >
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{f.parteNombre}</div>
                      <Etiqueta tono={f.unidadesEnExistencia === 0 ? 'roja' : 'ambar'}>
                        {f.unidadesEnExistencia}
                      </Etiqueta>
                    </div>
                    <div className="tarjeta-ot-nota">{f.idPieza}</div>
                    <div className="tarjeta-ot-datos">
                      <span>{f.ubicacionNombre}</span>
                      {f.area ? <span>{f.area}</span> : null}
                      <span>Mínimo {f.minimoEnExistencia}</span>
                      <span>Reponer {f.sugeridoReponer}</span>
                      {f.gruposACargo.length > 0 ? <span>{f.gruposACargo.join(', ')}</span> : null}
                    </div>
                  </div>
                  {puede('parte:transaccionar') ? (
                    <div className="acciones-fila" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="boton pequeno" onClick={() => setReponiendo(f)}>
                        Reponer
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Parte</th>
                    <th>Ubicación</th>
                    <th>Existencias</th>
                    <th>Mínimo</th>
                    <th>Sugerido reponer</th>
                    <th>Grupo a cargo</th>
                    {puede('parte:transaccionar') ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.map((f) => (
                    <tr key={`${f.parteId}-${f.ubicacionId}`}>
                      <td>
                        <EnlaceRuta to={`/partes/${f.parteId}`} style={{ fontWeight: 600 }}>
                          {f.parteNombre}
                        </EnlaceRuta>
                        <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{f.idPieza}</div>
                      </td>
                      <td>
                        {f.ubicacionNombre}
                        {f.area ? (
                          <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{f.area}</div>
                        ) : null}
                      </td>
                      <td>
                        <Etiqueta tono={f.unidadesEnExistencia === 0 ? 'roja' : 'ambar'}>
                          {f.unidadesEnExistencia}
                        </Etiqueta>
                      </td>
                      <td>{f.minimoEnExistencia}</td>
                      <td style={{ fontWeight: 700 }}>{f.sugeridoReponer}</td>
                      <td>{f.gruposACargo.join(', ') || '—'}</td>
                      {puede('parte:transaccionar') ? (
                        <td>
                          <button
                            type="button"
                            className="boton pequeno"
                            onClick={() => setReponiendo(f)}
                          >
                            Reponer
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {reponiendo ? (
        <DialogoMovimiento
          parteId={reponiendo.parteId}
          titulo={`Reponer ${reponiendo.parteNombre}`}
          ubicaciones={[
            { ubicacionId: reponiendo.ubicacionId, ubicacionNombre: reponiendo.ubicacionNombre },
          ]}
          tipoFijo="ENTRADA"
          cantidadInicial={reponiendo.sugeridoReponer}
          alCerrar={() => setReponiendo(null)}
          alGuardar={() => {
            void cliente.invalidateQueries({ queryKey: ['reposicion'] });
            void cliente.invalidateQueries({ queryKey: ['partes'] });
            setReponiendo(null);
          }}
        />
      ) : null}
    </>
  );
}

// ─── Ficha de la parte (§6.3) ────────────────────────────────────────────────

type Pestana = 'ficha' | 'existencias' | 'historial';

export function DetalleParte(): JSX.Element {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [pestana, setPestana] = useState<Pestana>('ficha');
  const [movimiento, setMovimiento] = useState<'ENTRADA' | 'SALIDA' | 'AJUSTE' | null>(null);
  const [transfiriendo, setTransfiriendo] = useState(false);
  const [editando, setEditando] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const { data: parte, isLoading } = useQuery({
    queryKey: ['parte', id],
    queryFn: () => api.get<Parte>(`/partes/${id}`),
    enabled: Boolean(id),
  });

  const refrescar = (): void => {
    void cliente.invalidateQueries({ queryKey: ['parte', id] });
    void cliente.invalidateQueries({ queryKey: ['reposicion'] });
    void cliente.invalidateQueries({ queryKey: ['transacciones', id] });
    setMovimiento(null);
    setTransfiriendo(false);
  };

  const eliminar = useMutation({
    mutationFn: () => api.delete(`/partes/${id}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['partes'] });
      await cliente.invalidateQueries({ queryKey: ['reposicion'] });
      // `replace`: la ficha de la parte eliminada no debe quedar en el historial.
      navegar('/partes', { replace: true });
    },
  });

  if (isLoading) return <Cargando />;
  if (!parte) return <Vacio titulo="La parte no existe" />;

  return (
    <>
      <BotonVolver a="/partes" etiqueta="Volver a Partes" />

      <div className="cabecera-pagina">
        <div>
          <h1>
            {parte.nombre}
            {parte.eliminadoEl ? (
              <span style={{ marginLeft: 8, display: 'inline-block' }}>
                <Etiqueta tono="gris">Eliminada</Etiqueta>
              </span>
            ) : null}
          </h1>
          <p>
            ID de pieza <strong>{parte.idPieza}</strong>
            {parte.numeroParte ? ` · N.º de parte ${parte.numeroParte}` : ''} ·{' '}
            {formatearMoneda(parte.costoUnitarioPen)} por unidad
          </p>
        </div>
        {/* Una parte eliminada no admite nuevos movimientos ni edición (el
            servidor ya lo rechaza); se ocultan los botones en vez de dejar
            que el usuario los pulse para encontrarse recién ahí con el error
            (§ bug reportado: no había ningún aviso en la ficha). */}
        {!parte.eliminadoEl ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {puede('parte:transaccionar') ? (
              <>
                <button type="button" className="boton" onClick={() => setMovimiento('ENTRADA')}>
                  Reponer
                </button>
                <button type="button" className="boton secundario" onClick={() => setMovimiento('AJUSTE')}>
                  Ajustar
                </button>
                <button
                  type="button"
                  className="boton secundario"
                  onClick={() => setTransfiriendo(true)}
                  disabled={parte.stock.length < 2}
                  title={parte.stock.length < 2 ? 'Se necesitan al menos dos ubicaciones' : undefined}
                >
                  Transferir
                </button>
              </>
            ) : null}
            {puede('parte:escribir') ? (
              <>
                <button type="button" className="boton secundario" onClick={() => setEditando(true)}>
                  Editar ficha
                </button>
                <button type="button" className="boton secundario" onClick={() => setEliminando(true)}>
                  Eliminar
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {parte.eliminadoEl ? (
        <Aviso tono="atencion">
          Esta parte está eliminada; conserva su historial pero no admite nuevos movimientos.
        </Aviso>
      ) : null}

      {parte.ubicacionesEnAlerta > 0 ? (
        <Aviso tono="atencion">
          {parte.ubicacionesEnAlerta} de {parte.stock.length} ubicación(es) están en su mínimo o por
          debajo, con {parte.totalEnExistencia} unidades en total.
        </Aviso>
      ) : null}

      <div className="filtros" role="tablist">
        {(
          [
            ['ficha', 'Ficha'],
            ['existencias', `Existencias (${parte.stock.length})`],
            ['historial', 'Historial'],
          ] as [Pestana, string][]
        ).map(([clave, texto]) => (
          <button
            key={clave}
            type="button"
            role="tab"
            aria-selected={pestana === clave}
            className={pestana === clave ? 'boton pequeno' : 'boton secundario pequeno'}
            onClick={() => setPestana(clave)}
          >
            {texto}
          </button>
        ))}
      </div>

      {pestana === 'ficha' ? <Ficha parte={parte} /> : null}
      {pestana === 'existencias' ? <Existencias parte={parte} alGuardar={refrescar} /> : null}
      {pestana === 'historial' ? <Historial parteId={parte.id} /> : null}

      {movimiento ? (
        <DialogoMovimiento
          parteId={parte.id}
          titulo={movimiento === 'ENTRADA' ? `Reponer ${parte.nombre}` : `Ajustar ${parte.nombre}`}
          ubicaciones={parte.stock}
          tipoFijo={movimiento}
          costoSugerido={parte.costoUnitarioPen}
          alCerrar={() => setMovimiento(null)}
          alGuardar={refrescar}
        />
      ) : null}

      {transfiriendo ? (
        <DialogoTransferencia parte={parte} alCerrar={() => setTransfiriendo(false)} alGuardar={refrescar} />
      ) : null}

      {editando ? (
        <FormularioParte
          parte={parte}
          alCerrar={() => setEditando(false)}
          alGuardar={async () => {
            await cliente.invalidateQueries({ queryKey: ['parte', id] });
            await cliente.invalidateQueries({ queryKey: ['partes'] });
            setEditando(false);
          }}
        />
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar parte"
          mensaje={`Se retirará «${parte.nombre}» del catálogo. Las transacciones registradas se conservan: son inmutables.`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(false);
          }}
          alConfirmar={() => eliminar.mutate()}
        />
      ) : null}
    </>
  );
}

function Ficha({ parte }: { parte: Parte }): JSX.Element {
  return (
    <div className="editor">
      <section className="panel">
        <div className="panel-cabecera">
          <h2>Identificación</h2>
        </div>
        <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <Dato titulo="Descripción" valor={parte.descripcion ?? 'Sin descripción'} />
          <Dato titulo="Costo unitario" valor={formatearMoneda(parte.costoUnitarioPen)} />
          <Dato titulo="Tipos de parte" valor={parte.tiposParte.map((t) => t.nombre).join(', ') || '—'} />
          <Dato
            titulo="Código QR"
            valor={
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>
                {parte.codigoQr ?? '—'}
              </span>
            }
          />
        </div>
      </section>

      <aside className="panel" style={{ alignSelf: 'start' }}>
        <div className="panel-cabecera">
          <h2>Compatibilidad y responsabilidad</h2>
        </div>
        <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {/* §6.2: la compatibilidad se expresa como relación con Tipos de Activo,
              en lugar del sufijo del nombre —«Vidrio de cabezal (C1-C2-C3-C4)»—,
              que obliga a leer el catálogo para saber qué sirve para qué. */}
          <Dato
            titulo="Tipos de activo compatibles"
            valor={
              parte.tiposActivoCompatibles.length === 0 ? (
                'Sin definir'
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {parte.tiposActivoCompatibles.map((t) => (
                    <Etiqueta key={t.id} tono="azul">{t.nombre}</Etiqueta>
                  ))}
                </div>
              )
            }
          />
          <Dato
            titulo="Activos compatibles (excepciones)"
            valor={
              parte.activosCompatibles.length === 0
                ? 'Ninguna'
                : parte.activosCompatibles.map((a) => a.nombre).join(', ')
            }
          />
          <Dato
            titulo="Grupos a cargo"
            valor={parte.gruposACargo.map((g) => g.nombre).join(', ') || 'Sin asignar'}
          />
        </div>
      </aside>
    </div>
  );
}

function Existencias({ parte, alGuardar }: { parte: Parte; alGuardar: () => void }): JSX.Element {
  const { puede } = useSesion();
  const [editando, setEditando] = useState<StockEnUbicacion | null>(null);

  return (
    <>
      <div className="panel">
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Ubicación</th>
                <th>Área</th>
                <th>Existencias</th>
                <th>Mínimo</th>
                <th>Máximo</th>
                <th>Estado</th>
                {puede('parte:escribir') ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {parte.stock.map((s) => (
                <tr key={s.ubicacionId}>
                  <td>
                    <span style={{ fontWeight: 600 }}>{s.ubicacionNombre}</span>
                    {s.ubicacionRaiz ? (
                      <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{s.ubicacionRaiz}</div>
                    ) : null}
                  </td>
                  <td>{s.area ?? '—'}</td>
                  <td style={{ fontWeight: 700 }}>{s.unidadesEnExistencia}</td>
                  <td>{s.minimoEnExistencia}</td>
                  <td>{s.maximoEnExistencia ?? '—'}</td>
                  <td>
                    {s.necesitaReposicion ? (
                      <Etiqueta tono="roja">Necesita reposición</Etiqueta>
                    ) : (
                      <Etiqueta tono="verde">Suficiente</Etiqueta>
                    )}
                  </td>
                  {puede('parte:escribir') ? (
                    <td>
                      <button type="button" className="boton secundario pequeno" onClick={() => setEditando(s)}>
                        Umbrales
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editando ? (
        <DialogoUmbrales
          parteId={parte.id}
          stock={editando}
          alCerrar={() => setEditando(null)}
          alGuardar={() => {
            setEditando(null);
            alGuardar();
          }}
        />
      ) : null}
    </>
  );
}

function Historial({ parteId }: { parteId: string }): JSX.Element {
  const [pagina, setPagina] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['transacciones', parteId, pagina],
    queryFn: () =>
      api.get<Pagina<Transaccion>>(`/partes/${parteId}/transacciones${consulta({ pagina, tamano: 25 })}`),
  });

  return (
    <div className="panel">
      {isLoading ? <Cargando /> : null}
      {data && data.datos.length === 0 ? (
        <Vacio titulo="Sin movimientos registrados" detalle="Las existencias son la suma de este libro." />
      ) : null}

      {data && data.datos.length > 0 ? (
        <>
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Ubicación</th>
                  <th>Cantidad</th>
                  <th>Costo unitario</th>
                  <th>Total</th>
                  <th>Origen</th>
                  <th>Registró</th>
                </tr>
              </thead>
              <tbody>
                {data.datos.map((t) => (
                  <tr key={t.id}>
                    <td>{formatearFechaHora(t.fechaHora)}</td>
                    <td>
                      <Etiqueta tono={tonoDe(t)}>{ETIQUETA_TIPO_TRANSACCION_PARTE[t.tipo]}</Etiqueta>
                    </td>
                    <td>
                      {t.ubicacionNombre}
                      {t.tipo === 'TRANSFERENCIA' && t.ubicacionDestinoNombre ? (
                        <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                          Destino: {t.ubicacionDestinoNombre}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontWeight: 700, color: t.cantidad < 0 ? 'var(--falla)' : 'var(--exito)' }}>
                      {t.cantidad > 0 ? `+${t.cantidad}` : t.cantidad}
                    </td>
                    {/* §6.5: el costo quedó congelado en la fecha del movimiento. */}
                    <td>{formatearMoneda(t.costoUnitarioPen)}</td>
                    <td>{formatearMoneda(t.costoTotalPen)}</td>
                    <td>
                      {t.ordenTrabajoId ? (
                        <EnlaceRuta to={`/ordenes/${t.ordenTrabajoId}`}>OT #{t.ordenTrabajoNumero}</EnlaceRuta>
                      ) : (
                        (t.nota ?? '—')
                      )}
                    </td>
                    <td>{t.usuario ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
        </>
      ) : null}
    </div>
  );
}

// ─── Diálogos ────────────────────────────────────────────────────────────────

function DialogoMovimiento({
  parteId,
  titulo,
  ubicaciones,
  tipoFijo,
  cantidadInicial,
  costoSugerido,
  alCerrar,
  alGuardar,
}: {
  parteId: string;
  titulo: string;
  ubicaciones: { ubicacionId: string; ubicacionNombre: string }[];
  tipoFijo: 'ENTRADA' | 'SALIDA' | 'AJUSTE';
  cantidadInicial?: number;
  costoSugerido?: string;
  alCerrar: () => void;
  alGuardar: () => void;
}): JSX.Element {
  const [ubicacionId, setUbicacionId] = useState(ubicaciones[0]?.ubicacionId ?? '');
  const [cantidad, setCantidad] = useState(String(cantidadInicial ?? 1));
  const [costo, setCosto] = useState(costoSugerido ?? '');
  const [nota, setNota] = useState('');

  const guardar = useMutation({
    mutationFn: () =>
      api.post(`/partes/${parteId}/transacciones`, {
        tipo: tipoFijo,
        ubicacionId,
        // El signo lo fija el tipo: pedirle al usuario que escriba «-3» para una
        // salida es una fuente de errores que el formulario puede evitar.
        cantidad: tipoFijo === 'SALIDA' ? -Math.abs(Number(cantidad)) : Number(cantidad),
        costoUnitarioPen: costo === '' ? undefined : Number(costo),
        nota: nota || undefined,
      }),
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={titulo} alCerrar={alCerrar}>
      <div className="campo">
        <label htmlFor="mov-ubicacion">Ubicación</label>
        <select id="mov-ubicacion" value={ubicacionId} onChange={(e) => setUbicacionId(e.target.value)}>
          {ubicaciones.map((u) => (
            <option key={u.ubicacionId} value={u.ubicacionId}>
              {u.ubicacionNombre}
            </option>
          ))}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="mov-cantidad">
          {tipoFijo === 'AJUSTE' ? 'Diferencia (negativa para descontar)' : 'Cantidad'}
        </label>
        <input
          id="mov-cantidad"
          type="number"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
      </div>

      {tipoFijo === 'ENTRADA' ? (
        <div className="campo">
          <label htmlFor="mov-costo">Costo unitario (S/)</label>
          <input id="mov-costo" type="number" step="0.01" value={costo} onChange={(e) => setCosto(e.target.value)} />
          <span style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
            Queda congelado en esta transacción; no reescribe el costo del catálogo.
          </span>
        </div>
      ) : null}

      <div className="campo">
        <label htmlFor="mov-nota">Nota</label>
        <textarea id="mov-nota" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>

      {guardar.isError ? <Aviso tono="error">{(guardar.error as Error).message}</Aviso> : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={!ubicacionId || Number(cantidad) === 0 || Number.isNaN(Number(cantidad))}
      />
    </Dialogo>
  );
}

function DialogoTransferencia({
  parte,
  alCerrar,
  alGuardar,
}: {
  parte: Parte;
  alCerrar: () => void;
  alGuardar: () => void;
}): JSX.Element {
  const [origen, setOrigen] = useState(parte.stock[0]?.ubicacionId ?? '');
  const [destino, setDestino] = useState(parte.stock[1]?.ubicacionId ?? '');
  const [cantidad, setCantidad] = useState('1');
  const [nota, setNota] = useState('');

  const disponible = parte.stock.find((s) => s.ubicacionId === origen)?.unidadesEnExistencia ?? 0;

  const guardar = useMutation({
    mutationFn: () =>
      api.post('/inventario/transferencias', {
        parteId: parte.id,
        ubicacionOrigenId: origen,
        ubicacionDestinoId: destino,
        cantidad: Number(cantidad),
        nota: nota || undefined,
      }),
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={`Transferir ${parte.nombre}`} alCerrar={alCerrar}>
      <div className="campo">
        <label htmlFor="tr-origen">Desde</label>
        <select id="tr-origen" value={origen} onChange={(e) => setOrigen(e.target.value)}>
          {parte.stock.map((s) => (
            <option key={s.ubicacionId} value={s.ubicacionId}>
              {s.ubicacionNombre} · {s.unidadesEnExistencia} unidad(es)
            </option>
          ))}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="tr-destino">Hacia</label>
        <select id="tr-destino" value={destino} onChange={(e) => setDestino(e.target.value)}>
          {parte.stock
            .filter((s) => s.ubicacionId !== origen)
            .map((s) => (
              <option key={s.ubicacionId} value={s.ubicacionId}>
                {s.ubicacionNombre}
              </option>
            ))}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="tr-cantidad">Cantidad (disponible: {disponible})</label>
        <input
          id="tr-cantidad"
          type="number"
          min={1}
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
      </div>

      <div className="campo">
        <label htmlFor="tr-nota">Nota</label>
        <textarea id="tr-nota" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>

      {guardar.isError ? <Aviso tono="error">{(guardar.error as Error).message}</Aviso> : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={!origen || !destino || origen === destino || Number(cantidad) < 1}
      />
    </Dialogo>
  );
}

function DialogoUmbrales({
  parteId,
  stock,
  alCerrar,
  alGuardar,
}: {
  parteId: string;
  stock: StockEnUbicacion;
  alCerrar: () => void;
  alGuardar: () => void;
}): JSX.Element {
  const [area, setArea] = useState(stock.area ?? '');
  const [minimo, setMinimo] = useState(String(stock.minimoEnExistencia));
  const [maximo, setMaximo] = useState(stock.maximoEnExistencia === null ? '' : String(stock.maximoEnExistencia));

  const guardar = useMutation({
    mutationFn: () =>
      api.put(`/partes/${parteId}/stock/${stock.ubicacionId}`, {
        area: area || undefined,
        minimoEnExistencia: Number(minimo),
        maximoEnExistencia: maximo === '' ? null : Number(maximo),
      }),
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={`Umbrales en ${stock.ubicacionNombre}`} alCerrar={alCerrar}>
      <div className="campo">
        <label htmlFor="um-area">Área</label>
        <input
          id="um-area"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          placeholder="Estante o posición dentro de la ubicación"
        />
      </div>

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="um-min">Mínimo (umbral de alerta)</label>
          <input id="um-min" type="number" min={0} value={minimo} onChange={(e) => setMinimo(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="um-max">Máximo (objetivo de reposición)</label>
          <input id="um-max" type="number" min={0} value={maximo} onChange={(e) => setMaximo(e.target.value)} />
        </div>
      </div>

      <p style={{ fontSize: 12, color: 'var(--texto-tenue)', marginTop: -4 }}>
        Las existencias no se editan aquí: son la suma de las transacciones. Para corregir un desfase,
        registre un ajuste.
      </p>

      {guardar.isError ? <Aviso tono="error">{(guardar.error as Error).message}</Aviso> : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={minimo === '' || Number(minimo) < 0}
      />
    </Dialogo>
  );
}

// ─── Piezas locales ──────────────────────────────────────────────────────────

function Dato({ titulo, valor }: { titulo: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: 'var(--texto-tenue)' }}>
        {titulo.toUpperCase()}
      </div>
      <div style={{ marginTop: 3, fontSize: 13.5 }}>{valor}</div>
    </div>
  );
}

function tonoDe(t: Transaccion): 'verde' | 'roja' | 'ambar' | 'azul' {
  if (t.tipo === 'ENTRADA') return 'verde';
  if (t.tipo === 'SALIDA') return 'roja';
  if (t.tipo === 'AJUSTE') return 'ambar';
  return 'azul';
}

// ─── Alta y edición de la parte (§6.3) ───────────────────────────────────────

/**
 * Ficha de una parte.
 *
 * Las existencias no se editan aquí: `unidades_en_existencia` es la suma de las
 * transacciones y el rol de aplicación ni siquiera tiene UPDATE sobre esa
 * columna (§6.6). Se ajusta con un movimiento, que deja rastro.
 */
function FormularioParte({
  parte,
  alCerrar,
  alGuardar,
}: {
  parte: Parte | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const { puede } = useSesion();
  const [nombre, setNombre] = useState(parte?.nombre ?? '');
  const [idPieza, setIdPieza] = useState(parte?.idPieza ?? '');
  const [numeroParte, setNumeroParte] = useState(parte?.numeroParte ?? '');
  const [descripcion, setDescripcion] = useState(parte?.descripcion ?? '');
  const [costoUnitarioPen, setCostoUnitarioPen] = useState(parte?.costoUnitarioPen ?? '0');
  const [codigoQr, setCodigoQr] = useState(parte?.codigoQr ?? '');
  const [escaneandoQr, setEscaneandoQr] = useState(false);
  const [tiposParte, setTiposParte] = useState<string[]>(parte?.tiposParte.map((t) => t.id) ?? []);
  const [tiposActivoCompatibles, setTiposActivoCompatibles] = useState<string[]>(
    parte?.tiposActivoCompatibles.map((t) => t.id) ?? [],
  );
  const [gruposACargo, setGruposACargo] = useState<string[]>(parte?.gruposACargo.map((g) => g.id) ?? []);

  const { data: catalogoTipos } = useCatalogo('TIPO_PARTE');
  const { data: tiposActivo } = useTiposActivo();
  const { data: grupos } = useGruposOpciones(puede('grupo:leer'));

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        nombre: nombre.trim(),
        idPieza: idPieza.trim(),
        numeroParte: numeroParte.trim(),
        descripcion: descripcion.trim(),
        costoUnitarioPen: costoUnitarioPen === '' ? 0 : Number(costoUnitarioPen),
        tiposParte,
        tiposActivoCompatibles,
        // La compatibilidad activo por activo es la excepción de §6.2 y se
        // administra desde la ficha; el alta no la toca.
        activosCompatibles: parte?.activosCompatibles.map((a) => a.id) ?? [],
        gruposACargo,
        codigoQr: codigoQr.trim(),
      };
      return parte ? api.put<Parte>(`/partes/${parte.id}`, cuerpo) : api.post<Parte>('/partes', cuerpo);
    },
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={parte ? `Editar ${parte.nombre}` : 'Nueva parte'} ancho={640} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="parte-nombre">Nombre</label>
        <input
          id="parte-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={250}
          required
          placeholder="Ej.: Filtro de agua del cabezal"
        />
      </div>

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="parte-id-pieza">ID de pieza</label>
          <input
            id="parte-id-pieza"
            value={idPieza}
            onChange={(e) => setIdPieza(e.target.value)}
            maxLength={60}
            required
            placeholder="El que el técnico lee en el estante"
          />
        </div>
        <div className="campo">
          <label htmlFor="parte-numero">N.º de parte del fabricante</label>
          <input
            id="parte-numero"
            value={numeroParte}
            onChange={(e) => setNumeroParte(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="campo">
          <label htmlFor="parte-costo">Costo unitario (S/)</label>
          <input
            id="parte-costo"
            type="number"
            min={0}
            step="0.01"
            value={costoUnitarioPen}
            onChange={(e) => setCostoUnitarioPen(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="parte-qr">Código QR</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="parte-qr"
              value={codigoQr}
              onChange={(e) => setCodigoQr(e.target.value)}
              maxLength={40}
              placeholder="Se genera solo si se deja vacío"
              style={{ flex: 1 }}
            />
            {hayCamara() ? (
              <button type="button" className="boton secundario pequeno" onClick={() => setEscaneandoQr(true)}>
                Escanear
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {escaneandoQr ? (
        <EscanerQr
          alCerrar={() => setEscaneandoQr(false)}
          alDetectar={(codigo) => {
            setCodigoQr(codigo);
            setEscaneandoQr(false);
          }}
        />
      ) : null}

      <SeleccionMultiple
        etiqueta="Tipos de parte"
        opciones={(catalogoTipos ?? []).filter((t) => t.activo || tiposParte.includes(t.id))}
        seleccion={tiposParte}
        alCambiar={setTiposParte}
        vacio="El catálogo «Tipos de parte» está vacío. Se administra en Catálogos."
      />

      <SeleccionMultiple
        etiqueta="Compatible con tipos de activo"
        opciones={(tiposActivo ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
        seleccion={tiposActivoCompatibles}
        alCambiar={setTiposActivoCompatibles}
        vacio="Aún no hay tipos de activo. Se crean en Catálogos."
      />

      {puede('grupo:leer') ? (
        <SeleccionMultiple
          etiqueta="Grupos a cargo"
          opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          seleccion={gruposACargo}
          alCambiar={setGruposACargo}
          vacio="Aún no hay grupos."
        />
      ) : null}

      <div className="campo">
        <label htmlFor="parte-descripcion">Descripción</label>
        <textarea
          id="parte-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={4000}
        />
      </div>

      {parte ? null : (
        <Aviso tono="info">
          Las existencias no se cargan aquí: se registran con un movimiento de entrada por ubicación,
          para que el stock siempre sea la suma de sus transacciones.
        </Aviso>
      )}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={nombre.trim() === '' || idPieza.trim() === ''}
        etiqueta={parte ? 'Guardar cambios' : 'Crear parte'}
      />
    </Dialogo>
  );
}
